import * as React from 'react';
import { mergeStyles } from '@uifabric/merge-styles';

const styles = require('./Toggle.scss');
const classNames = require('classnames/bind').bind(styles);

export interface ToggleProps {
    text: string;
    defaultChecked: boolean;
    className?: string;
    onChanged?: (checked: boolean) => void;
    /**
     * Prop to change the checked property of the toggle based on external changes
     */
    isChecked?: boolean;
    /**
     * If this value is set to true, clicking on the toggle does not change the state of toggle
     * This means that toggle is controlled by `isChecked` property
     */
    useCheckedStateFromProps?: boolean;
    /**
     * The style for the toggle pill
     */
    toggleStyles?: ToggleStyleProps;
    /**
     * isForcedAriaHidden : Set this if this component needs to be aria-hidden because an upper level component is handling it
     */
    isForcedAriaHidden?: boolean;
    /**
     * aria-label for the toggle
     */
    ariaLabel?: string;
}

export interface ToggleStyleProps {
    checkedBorderColor?: string;
    checkedBackgroundColor?: string;
    checkedThumbColor?: string;
    uncheckedBorderColor?: string;
    uncheckedBackgroundColor?: string;
    uncheckedThumbColor?: string;
}

export interface ToggleState {
    isChecked: boolean;
}

export class Toggle extends React.Component<ToggleProps, ToggleState> {
    constructor(props: ToggleProps) {
        super(props);
        const { defaultChecked, isChecked, useCheckedStateFromProps } = props;
        this.state = {
            isChecked: useCheckedStateFromProps ? isChecked : defaultChecked,
        };
    }

    render() {
        const {
            text,
            toggleStyles,
            useCheckedStateFromProps,
            isForcedAriaHidden,
            ariaLabel,
        } = this.props;
        const isChecked = this.getCheckedValue();
        const tabIndexProps =
            useCheckedStateFromProps && isForcedAriaHidden
                ? { 'aria-hidden': true }
                : { tabIndex: 0 };
        return (
            <div
                className={classNames(styles.buttonContainer, this.props.className)}
                role="button"
                {...tabIndexProps}
                aria-pressed={isChecked}
                aria-label={ariaLabel || text}
                onClick={this.onClick}>
                <div
                    className={classNames(
                        isChecked ? styles.checkedPill : styles.uncheckedPill,
                        toggleStyles &&
                            mergeStyles(
                                isChecked
                                    ? {
                                          background: this.getStyleValue(
                                              toggleStyles.checkedBackgroundColor
                                          ),
                                          borderColor: this.getStyleValue(
                                              toggleStyles.checkedBorderColor
                                          ),
                                      }
                                    : {
                                          background: this.getStyleValue(
                                              toggleStyles.uncheckedBackgroundColor
                                          ),
                                          borderColor: this.getStyleValue(
                                              toggleStyles.uncheckedBorderColor
                                          ),
                                      }
                            )
                    )}>
                    <div
                        className={classNames(
                            isChecked ? styles.checkedThumb : styles.uncheckedThumb,
                            toggleStyles &&
                                mergeStyles(
                                    isChecked
                                        ? {
                                              background: this.getStyleValue(
                                                  toggleStyles.checkedThumbColor
                                              ),
                                          }
                                        : {
                                              background: this.getStyleValue(
                                                  toggleStyles.uncheckedThumbColor
                                              ),
                                          }
                                )
                        )}
                    />
                </div>
                {text && <label className={styles.text}>{text}</label>}
            </div>
        );
    }

    private getStyleValue = (styleValue: string) =>
        styleValue ? `${styleValue} !important` : undefined;

    private onClick = () => {
        const newCheckedValue: boolean = !this.getCheckedValue();

        this.setState({
            isChecked: newCheckedValue,
        });

        if (this.props.onChanged) {
            this.props.onChanged(newCheckedValue);
        }
    };

    private getCheckedValue(): boolean {
        const { isChecked } = this.state;
        const { isChecked: isCheckedFromProps, useCheckedStateFromProps } = this.props;
        return useCheckedStateFromProps ? isCheckedFromProps : isChecked;
    }
}
